import { useState } from 'react';
import { Modal, Table, Button, Input, Row, Col, Card, Divider, message } from 'antd';
import { useMsal } from '@azure/msal-react';
import { updateAsset, getAssetById } from '../../../../Maintenance/helpers/apiCalls';

const MarkdownModal = ({ open, onClose, fileName, markdownContent, metadata, pdfUrl, assetId }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { instance } = useMsal();
  

  const getPropertyData = () => {
    const documentProperties = [];
    const assetProperties = [];
    let keyCounter = 1;

    // Parse document_info if available
    if (metadata?.document_info) {
      try {
        const docInfo = JSON.parse(metadata.document_info);
        Object.entries(docInfo).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            documentProperties.push({
              key: String(keyCounter++),
              propertyName: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              propertyInput: String(value),
              section: 'Document Information'
            });
          }
        });
      } catch (e) {
        console.error('Error parsing document_info:', e);
      }
    }

    // Parse asset_info if available
    if (metadata?.asset_info) {
      try {
        const assetInfo = JSON.parse(metadata.asset_info);
        Object.entries(assetInfo).forEach(([key, value]) => {
          if (key === 'properties' && Array.isArray(value)) {
            value.forEach(prop => {
              if (prop.property_value !== null && prop.property_value !== undefined && prop.property_value !== '') {
                assetProperties.push({
                  key: String(keyCounter++),
                  propertyName: prop.property_name || 'Unknown Property',
                  propertyInput: `${prop.property_value}${prop.property_unit ? ` ${prop.property_unit}` : ''}`,
                  section: 'Asset Information'
                });
              }
            });
          } else if (value !== null && value !== undefined && value !== '' && key !== 'properties') {
            assetProperties.push({
              key: String(keyCounter++),
              propertyName: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              propertyInput: String(value),
              section: 'Asset Information'
            });
          }
        });
      } catch (e) {
        console.error('Error parsing asset_info:', e);
      }
    }

    // Combine with separators
    const allProperties = [];
    
    if (documentProperties.length > 0) {
      allProperties.push(...documentProperties);
    }
    
    if (assetProperties.length > 0) {
      if (documentProperties.length > 0) {
        allProperties.push({
          key: 'separator',
          propertyName: '',
          propertyInput: '',
          isSeparator: true
        });
      }
      allProperties.push(...assetProperties);
    }

    // Return default data if no metadata available
    if (allProperties.length === 0) {
      return [
        {
          key: '1',
          propertyName: 'Document Title',
          propertyInput: 'Sample Document Title'
        },
        {
          key: '2', 
          propertyName: 'Author',
          propertyInput: 'John Doe'
        },
        {
          key: '3',
          propertyName: 'Version',
          propertyInput: '1.0.0'
        }
      ];
    }

    return allProperties;
  };

  const propertyData = getPropertyData();

  const columns = [
    {
      title: 'Property Name',
      dataIndex: 'propertyName',
      key: 'propertyName',
      width: '40%',
    },
    {
      title: 'Property Input',
      dataIndex: 'propertyInput',
      key: 'propertyInput',
      width: '60%',
      render: (text, record) => {
        if (record.isSeparator) {
          return <Divider style={{ margin: '8px 0', fontSize: '12px', fontWeight: 'bold' }}>Asset Information</Divider>;
        }
        return (
          <Input 
            defaultValue={text}
            size="small"
            placeholder="Enter value"
          />
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys.filter(key => key !== 'separator'));
    },
    type: 'checkbox',
    getCheckboxProps: (record) => ({
      disabled: record.isSeparator,
    }),
  };

  const handleInsert = async () => {
    if (!assetId) {
      message.error('Asset ID is required to update properties');
      return;
    }

    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one property to insert');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // First, get the current asset to preserve existing properties
      const currentAsset = await getAssetById(instance, assetId);
      
      // Get the selected properties and format them for the asset
      const selectedProperties = propertyData.filter(item => selectedRowKeys.includes(item.key));
      
      // Transform the selected properties to the required format
      const formattedProperties = selectedProperties.map(prop => ({
        label: prop.propertyName,
        type: "text",
        value: prop.propertyInput,
        main: false,
        required: false,
        key: prop.propertyName.toLowerCase().replace(/\s+/g, '_')
      }));

      // Append new properties to existing ones
      const existingProperties = currentAsset.properties || [];
      const updatedProperties = [...existingProperties, ...formattedProperties];

      // Create the payload with the complete properties array
      const payload = {
        ...currentAsset,
        properties: updatedProperties
      };

      await updateAsset(instance, assetId, payload);
      
      message.success(`Successfully added ${selectedProperties.length} properties to the asset`);
      
      // Reset selection and close modal
      setSelectedRowKeys([]);
      onClose();
      
    } catch (error) {
      console.error('Error updating asset:', error);
      message.error('Failed to update asset properties. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedRowKeys([]);
    onClose();
  };


  return (
    // TO-DO: Fix the modal so it scrolls correctly with the markdown content.
    <Modal
      title={`Document Viewer - ${fileName || 'Document'}`}
      open={open}
      onCancel={handleCancel}
      width={1200}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Close
        </Button>,
      ]}
      style={{ top: 20 }}
      styles={{ body: { height: '70vh', overflow: 'hidden' } }}
    >
      <Row gutter={16} style={{ height: '100%' }}> 
        <Col span={16}>
          <Card 
            title="PDF Document" 
            size="small" 
            style={{ height: '100%' }}
            styles={{ body: { 
                      padding: '0', 
                      height: '60vh', 
                      overflow: 'hidden' 
                    } }}
          >
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title={`PDF Viewer - ${fileName}`}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: '#666'
              }}>
                No PDF available to display
              </div>
            )}
          </Card>
        </Col>
        
        <Col span={8}>
          <Card 
            title="Properties" 
            size="small" 
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            styles={{ body: { padding: '16px', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
          >
            <div style={{ flex: 1, overflow: 'auto', marginBottom: '16px', maxHeight: '50vh' }}>
              <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={propertyData}
                pagination={false}
                size="small"
                scroll={{ y: 400 }}
                rowClassName={(record) => record.isSeparator ? 'separator-row' : ''}
              />
            </div>
            <Button 
              type="primary" 
              onClick={handleInsert}
              disabled={selectedRowKeys.length === 0 || isSubmitting}
              loading={isSubmitting}
              style={{ width: '100%' }}
            >
              {isSubmitting ? 'Updating Asset...' : 'Insert Selected Properties'}
            </Button>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default MarkdownModal;